import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";
import {
	createContext,
	ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";

interface UserProfile {
	id: string;
	name: string | null;
	businessName: string | null;
	accountType: string;
	description: string | null;
	location: string | null;
	profileImage: string | null;
	minimumFreeShipping: number;
	shippingCost: number | null;
	shippingCostType: string;
	minimumOrder: number;
	minDeliveryDays: number | null;
	maxDeliveryDays: number | null;
	website: string | null;
}

interface AuthContextType {
	session: Session | null;
	user: User | null;
	profile: UserProfile | null;
	isLoading: boolean;
	isSupplier: boolean;
	isRetailer: boolean;
	signOut: () => Promise<void>;
	refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
	const [session, setSession] = useState<Session | null>(null);
	const [user, setUser] = useState<User | null>(null);
	const [profile, setProfile] = useState<UserProfile | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		async function getInitialSession() {
			try {
				setIsLoading(true);

				// Get session
				const {
					data: { session: initialSession },
				} = await supabase.auth.getSession();
				setSession(initialSession);
				setUser(initialSession?.user ?? null);

				if (initialSession?.user) {
					await fetchProfile(initialSession.user.id);
				}

				// Listen for auth changes
				const {
					data: { subscription },
				} = supabase.auth.onAuthStateChange((_event, newSession) => {
					// Synchronously update session and user
					setSession(newSession);
					setUser(newSession?.user ?? null);

					// Defer profile fetching to avoid deadlocks
					if (newSession?.user) {
						setTimeout(() => {
							fetchProfile(newSession.user.id);
						}, 0);
					} else {
						setProfile(null);
					}
				});

				return () => {
					subscription.unsubscribe();
				};
			} catch (error) {
				console.error("Error getting initial session:", error);
			} finally {
				setIsLoading(false);
			}
		}

		getInitialSession();
	}, []);

	async function fetchProfile(userId: string) {
		try {
			const { data, error } = await supabase
				.from("profiles")
				.select("*")
				.eq("id", userId)
				.single();

			if (error) {
				throw error;
			}

			if (data) {
				const userProfile: UserProfile = {
					id: data.id,
					name: data.name,
					businessName: data.business_name,
					accountType: data.account_type,
					description: data.description,
					location: data.location,
					profileImage: data.profile_image,
					minimumFreeShipping: data.minimum_free_shipping,
					shippingCost: data.shipping_cost,
					shippingCostType: data.shipping_cost_type,
					minimumOrder: data.minimum_order,
					minDeliveryDays: data.min_delivery_days,
					maxDeliveryDays: data.max_delivery_days,
					website: data.website,
				};

				setProfile(userProfile);
			}
		} catch (error) {
			console.error("Error fetching profile:", error);
		}
	}

	const refreshProfile = async () => {
		if (user) {
			await fetchProfile(user.id);
		}
	};

	const signOut = async () => {
		await supabase.auth.signOut();
		setProfile(null);
	};

	const isSupplier = profile?.accountType === "supplier";
	const isRetailer = profile?.accountType === "retailer";

	const value = {
		session,
		user,
		profile,
		isLoading,
		isSupplier,
		isRetailer,
		signOut,
		refreshProfile,
	};

	return (
		<AuthContext.Provider value={value}>{children}</AuthContext.Provider>
	);
}

export function useAuth() {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
